import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import FormButtons from 'components/form/FormButtons';
import Button from 'components/Button';
import API from 'core/Api';
import { formatDate } from 'utils/helpers/formatters';
import { getUserInfo } from 'utils/auth';

interface IProps {
  methods: any;
  data: any;
  onSubmit: (data: any) => void;
  reportParams?: any;
}

type UserOption = {
  value: number;
  label: string;
  flutterBe: string | null;
  payrollId: string | null;
};

const ReportForm: FC<IProps> = ({ methods, data, onSubmit, reportParams }) => {
  const { t } = useTranslation();
  const { formState } = methods;
  const { isSubmitting } = formState;
  const [employees, setEmployees] = useState(data?.user || []);
  const [reportBtnLoading, setReportBtnLoading] = useState(false);
  const isFeatureClientF = getUserInfo().featureCustomFeatureForClientF;

  const isEqualArray = (a: any[], b: any[]): boolean => {
    if (a.length !== b.length) return false;
    const sortA = [...a].sort((x, y) => x.value.localeCompare?.(y.value ?? '') ?? x.value - y.value);
    const sortB = [...b].sort((x, y) => x.value.localeCompare?.(y.value ?? '') ?? x.value - y.value);
    return sortA.every((item, i) => item.value === sortB[i].value);
  };

  useEffect(() => {
    methods.setValue('date_from', new Date(new Date().setHours(0, 0, 0, 0)));
    methods.setValue('date_to', new Date());
  }, []);

  const groups = methods.watch('group_id');

  useEffect(() => {
    const groupIds = groups?.map((gr: any) => gr.value);

    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds) {
      const filteredEmployees = data?.user.filter((empl: any) => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag: boolean, currGr: number) => {
          return flag || groupIds.includes(currGr);
        }, false);
        return isInSelectedGroups;
      });

      setEmployees(filteredEmployees);
      methods.setValue('user_id', filteredEmployees);
    } else {
      setEmployees(data?.user);
      methods.setValue('user_id', null);
    }
  }, [groups]);

  const handleDownload = () => {
    setReportBtnLoading(true);
    const formValues = methods.getValues();

    const formattedData: Record<string, any> = {
      category_id: formValues.category_id?.map((category: any) => category.value),
      group_id: formValues.group_id?.map((group: any) => group.value),
      creator_id: formValues.creator_id?.map((creator: any) => creator.value),
      action_id: formValues.action_id?.map((action: any) => action.value),
      date_from: formValues.date_from && formatDate(formValues.date_from, 'YYY-mm-dd hh:ii:ss'),
      date_to: formValues.date_to && formatDate(formValues.date_to, 'YYY-mm-dd hh:ii:ss'),
    };

    const userIds = formValues.user_id?.map((user: any) => user.value);
    if (userIds?.length) {
      formattedData.user_id = userIds;
    }

    API.post('reports/document-actions/download', formattedData).then(res => {
      setReportBtnLoading(false);
      window.open(res.data.url, '_blank');
    });
  };

  const convertNamedObjectToOptions = (obj: Record<string, string | number> | undefined) => {
    if (!obj) return [];
    return Object.values(obj).map(val => ({
      value: val,
      label: String(val),
    }));
  };

  const flutterBeOptions = convertNamedObjectToOptions(data?.flutterBe);
  const payrollIdOptions = convertNamedObjectToOptions(data?.payrollId);

  const userMap = new Map<number, UserOption>(data.user.map((u: any) => [u.value, u]));
  const flutterMap = new Map<string, number[]>();
  const payrollMap = new Map<string, number[]>();

  data.user.forEach((u: any) => {
    if (u.flutterBe) {
      if (!flutterMap.has(u.flutterBe)) flutterMap.set(u.flutterBe, []);
      flutterMap.get(u.flutterBe)?.push(u.value);
    }
    if (u.payrollId) {
      if (!payrollMap.has(u.payrollId)) payrollMap.set(u.payrollId, []);
      payrollMap.get(u.payrollId)?.push(u.value);
    }
  });

  useEffect(() => {
    if (!isFeatureClientF) return;

    const sub = methods.watch((values: any, { name }: any) => {
      if (!name) return;

      const selectedUsers = values.user_id?.map((u: any) => u.value) || [];
      const selectedFlutter = values.flutter_be?.map((f: any) => f.value) || [];
      const selectedPayroll = values.payroll_id?.map((p: any) => p.value) || [];

      if (name === 'user_id') {
        const flutterSet = new Set<string>();
        const payrollSet = new Set<string>();

        selectedUsers.forEach((id: number) => {
          const user = userMap.get(id);
          if (user?.flutterBe) flutterSet.add(user.flutterBe);
          if (user?.payrollId) payrollSet.add(user.payrollId);
        });

        const newFlutterBe = [...flutterSet].map(f => ({ value: f, label: f }));
        const newPayrollId = [...payrollSet].map(p => ({ value: p, label: p }));
        const currentFlutterBe = methods.getValues('flutter_be') || [];
        const currentPayrollId = methods.getValues('payroll_id') || [];

        if (!isEqualArray(newFlutterBe, currentFlutterBe)) {
          methods.setValue('flutter_be', newFlutterBe, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
        if (!isEqualArray(newPayrollId, currentPayrollId)) {
          methods.setValue('payroll_id', newPayrollId, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
      }

      if (name === 'flutter_be') {
        const userIds = new Set<number>();
        const payrollSet = new Set<string>();

        selectedFlutter.forEach((f: string) => {
          flutterMap.get(f)?.forEach(id => userIds.add(id));
        });

        [...userIds].forEach(id => {
          const user = userMap.get(id);
          if (user?.payrollId) payrollSet.add(user.payrollId);
        });

        const newUserIds = [...userIds].map(id => {
          const u = userMap.get(id)!;
          return { value: id, label: u.label };
        });
        const currentUserIds = methods.getValues('user_id') || [];

        if (!isEqualArray(newUserIds, currentUserIds)) {
          methods.setValue('user_id', newUserIds, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }

        const newPayrollId = [...payrollSet].map(p => ({ value: p, label: p }));
        const currentPayrollId = methods.getValues('payroll_id') || [];
        if (!isEqualArray(newPayrollId, currentPayrollId)) {
          methods.setValue('payroll_id', newPayrollId, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
      }

      if (name === 'payroll_id') {
        const userIds = new Set<number>();
        const flutterSet = new Set<string>();

        selectedPayroll.forEach((p: string) => {
          payrollMap.get(p)?.forEach(id => userIds.add(id));
        });

        [...userIds].forEach(id => {
          const user = userMap.get(id);
          if (user?.flutterBe) flutterSet.add(user.flutterBe);
        });

        methods.setValue(
          'user_id',
          [...userIds].map(id => {
            const u = userMap.get(id)!;
            return { value: id, label: u.label };
          }),
          { shouldDirty: false, shouldValidate: false }
        );
        methods.setValue(
          'flutter_be',
          [...flutterSet].map(f => ({ value: f, label: f })),
          { shouldDirty: false, shouldValidate: false }
        );
      }
    });

    return () => sub.unsubscribe?.();
  }, []);

  useEffect(() => {
    const archivedAction = data?.actions?.find((action: any) => action.value === 'archived-by-employer');
    if (archivedAction) {
      methods.setValue('action_id', [archivedAction]);
    }
  }, [data?.actions]);

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.submitBtn')}
      hideAllBtns
      onSubmit={onSubmit}
    >
      <Row>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupCategories"
            label={t('reports.categories')}
            type="select"
            as="select"
            name="category_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.category}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            selectOptions={employees}
            limitOptions
          />
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupCreator"
            label={t('reports.author')}
            type="select"
            as="select"
            name="creator_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.creator}
          />
        </Col>
        <Col lg={4}>
          <Row>
            <Col md={6}>
              <FormGroup
                controlId="formGroupFromDate"
                label={t('reports.fromDate')}
                type="datepicker"
                name="date_from"
                maxDate={new Date()}
                dateFormat="dd.MM.yyyy HH:mm"
                showTimeSelect
                showMonthDropdown
                showYearDropdown
                timeIntervals={10}
              />
            </Col>
            <Col md={6}>
              <FormGroup
                controlId="formGroupToDate"
                label={t('reports.toDate')}
                type="datepicker"
                name="date_to"
                maxDate={new Date()}
                dateFormat="dd.MM.yyyy HH:mm"
                showTimeSelect
                showMonthDropdown
                showYearDropdown
                timeIntervals={10}
              />
            </Col>
          </Row>
        </Col>
        <Col lg={4}>
          <FormGroup
            controlId="formGroupActions"
            label={t('reports.actions')}
            type="select"
            as="select"
            name="action_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.actions}
          />
        </Col>
      </Row>
      {isFeatureClientF && (
        <Row>
          <Col
            lg={{ span: 6 }}
            xl={{
              offset: 2,
              span: 4,
            }}
          >
            <FormGroup
              controlId="formGroupFlutterBe"
              label="FlutterBe ID"
              type="select"
              as="select"
              name="flutter_be"
              isMulti
              isClearable
              isSearchable
              allowSelectAll
              selectOptions={flutterBeOptions}
              limitOptions
            />
          </Col>
          <Col
            lg={{ span: 6 }}
            xl={4}
          >
            <FormGroup
              controlId="formGroupPayrollId"
              label="Payroll ID"
              type="select"
              as="select"
              name="payroll_id"
              isMulti
              isClearable
              isSearchable
              allowSelectAll
              selectOptions={payrollIdOptions}
              limitOptions
            />
          </Col>
        </Row>
      )}
      <div className="d-flex flex-column flex-sm-row justify-content-center mb-5">
        <FormButtons
          btnLabel={t('reports.viewDocumentsByAction')}
          isSubmitting={isSubmitting}
          hideCancelBtn
        />
        <div className="pt-2 pl-sm-3 text-center text-sm-auto">
          <Button
            btnType="secondary"
            disabled={reportBtnLoading}
            loading={reportBtnLoading}
            onClick={handleDownload}
          >
            <FaDownload
              size={14}
              className="mr-2"
            />
            {t('reports.downloadDocumentsByAction')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default ReportForm;
